import React, { useState, useEffect, useRef } from "react";
import axiosInstance, { fetchCsrfToken } from "../utils/axiosInstance";
import { ApiUrl, ApiSendStatus } from "../data-types/api-client/api-url";
import { Mail } from "../data-types/api-client/api-contacts";
import MessageModal from "./MessageModal";
import ReCAPTCHA from "react-google-recaptcha";
import Loading from "../components/Loading";

type ResponseStatus = "success" | "error" | null;

const ReservationForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [formData, setFormData] = useState<Mail>({
    name: "",
    email: "",
    phone: "",
    text: "",
    recaptchaToken: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isFormVisible, setFormVisible] = useState(true); // or false if default hidden

  const [modalTitle, setModalTitle] = useState<string>("");
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [responseStatus, setResponseStatus] = useState<ResponseStatus>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const setModal = (status: string) => {
    switch (status) {
      case ApiSendStatus.OK:
        setResponseStatus("success");
        setModalTitle("Dakujeme za rezerváciu!");
        setResponseMessage("Čoskoro Vás budeme kontaktovať pre ďalšie pokyny.");
        break;
      case ApiSendStatus.ERROR:
        setResponseStatus("error");
        setModalTitle("Chyba pri rezervácii!");
        setResponseMessage(
          "Nepodarilo sa potvrdiť Vašu rezerváciu. Skúste to prosím neskôr."
        );
        break;
      case ApiSendStatus.ERROR_TOO_MANY_ATTEMPTS:
        setResponseStatus("error");
        setModalTitle("Chyba pri rezervácii!");
        setResponseMessage("Príliš veľa pokusov. Skúste to prosím neskôr.");
        break;
      default:
        setResponseStatus("error");
        setModalTitle("Chyba pri rezervácii!");
        setResponseMessage(
          "Nepodarilo sa potvrdiť Vašu rezerváciu. Skúste to prosím neskôr."
        );
    }
  };

  const resetModal = () => {
    setModalTitle("");
    setResponseMessage("");
    setResponseStatus(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Prosím, overte reCAPTCHA.");
      return;
    }

    resetModal();
    setIsModalOpen(false);

    const payload = {
      ...formData,
      recaptchaToken: captchaToken,
    };

    setLoading(true);

    axiosInstance
      .post<{ status: string }>(`${ApiUrl.ContactsApiUrl}/send`, payload)
      .then((res) => {
        const response = res.data.status;
        setModal(response);
        setIsModalOpen(true);

        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      })

      .catch((err) => {
        console.error(err);
        setModal(err.response.data.status);
        setIsModalOpen(true);

        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      })

      .finally(() => {
        setLoading(false);
      });

    setFormData({
      name: "",
      email: "",
      phone: "",
      text: "",
      recaptchaToken: "",
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // setFormVisible(false); // or true, depending on your flow
  };

  useEffect(() => {
    fetchCsrfToken().catch((err) => console.error("CSRF fetch failed", err));
  }, []);

  return (
    <>
      <div className="flex flex-col justify-center items-center md:w-[25rem] xs:w-[20rem] w-full h-auto py-5 mt-8">
        <h2 className="text-xl font-semibold mb-4 text-center">
          Online rezervácia
        </h2>

        <form onSubmit={handleSubmit} className="w-full mx-8">
          <div className="mb-4">
            <label
              htmlFor="name"
              className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
            >
              <span>Meno a Priezvisko / Názov firmy</span>
              <span className="text-custom-orange">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full p-2 bg-custom-gray border-b-2 border-b-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-custom-atrament rounded-none"
              placeholder="Vaše Meno a Priezvisko / Názov firmy"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="email"
              className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
            >
              <span>Email</span>
              <span className="text-custom-orange">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full p-2 bg-custom-gray border-b-2 border-b-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-custom-atrament rounded-none"
              placeholder="Váš email"
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="phone"
              className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
            >
              <span>Telefónne číslo</span>
              <span className="text-custom-orange">*</span>
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full p-2 bg-custom-gray border-b-2 border-b-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-custom-atrament rounded-none"
              placeholder="Vaše telefónne číslo"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="text"
              className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
            >
              <span>Popíšte, o aký produkt / službu máte záujem</span>
              <span className="text-custom-orange">*</span>
            </label>
            <textarea
              id="text"
              name="text"
              maxLength={1024}
              value={formData.text}
              onChange={handleChange}
              required
              rows={4}
              className="w-full h-auto p-2 bg-custom-gray text-gray-700 border-2 border-b-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-custom-atrament"
              placeholder="Chcel by som si rezervovať ..."
            />
          </div>

          <div className="w-full flex items-center justify-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ""}
              onChange={handleCaptchaChange}
              ref={recaptchaRef}
              className="mb-4"
            />
          </div>

          <button
            type="submit"
            className="flex md:w-40 w-full p-2 border-2 border-gray-300 justify-self-center items-center justify-center font-semibold text-gray-700 hover:bg-custom-red hover:border-custom-red hover:text-custom-white transition duration-200"
          >
            Odoslať
          </button>
          {loading && <Loading />}
        </form>
        <MessageModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={modalTitle}
          message={responseMessage}
          success={responseStatus}
        />
      </div>
    </>
  );
};

export default ReservationForm;
